import {
    useState
} from "react";
import {
    ComponentHeader,
    AnatomySection,
    ExampleSection,
    getSectionToc,
    PropsTable
} from "./shared";
import {
    NCoreUIKitTheme,
    CodeViewer,
    Switch,
    Button,
    Card
} from "ncore-ui-kit";
import {
    useDocsI18n
} from "../../i18n";
import type {
    DocsTranslate
} from "../../anatomy/types";
import {
    BulletList,
    Paragraph,
    CodeBlock,
    Article,
    Preview,
    Section
} from "../../components/content";
import {
    SearchIcon
} from "../../anatomy/icons";

const NAME = "CodeViewer";

const USAGE_CODE = `<CodeViewer
    isLineNumberVisible={true}
    isCollapsible={true}
    language="typescript"
    code={snippet}
/>

<CodeViewer
    isLineNumberVisible={false}
    languageLabel="Terminal"
    code="npm install ncore-ui-kit"
    language="bash"
/>`;

const FOLDING_CODE = `<CodeViewer
    language="typescript"
    autoFoldMaxLines={2}
    autoFold={true}
    code={snippet}
/>`;

const REPORT_CODE = [
    "type Invoice = {",
    "    amount: number;",
    "    isPaid: boolean;",
    "};",
    "",
    "export const getReport = (invoices: Array<Invoice>) => {",
    "    const paid = invoices.filter((invoice) => invoice.isPaid);",
    "    const unpaid = invoices.filter((invoice) => !invoice.isPaid);",
    "",
    "    return {",
    "        paidTotal: getTotal(paid),",
    "        unpaidTotal: getTotal(unpaid),",
    "        count: invoices.length",
    "    };",
    "};"
].join("\n");

const BASH_CODE = "npm install ncore-ui-kit";

const SQL_CODE = [
    "SELECT name, SUM(amount) AS total",
    "FROM invoices",
    "WHERE is_paid = true",
    "GROUP BY name;"
].join("\n");

const PYTHON_CODE = [
    "def get_total(rows):",
    "    return sum(row.amount for row in rows)"
].join("\n");

const HEADER_CODE = `<CodeViewer
    lineStates={lineStates}
    language="typescript"
    code={snippet}
    startLine={41}
>
    <CodeViewer.Header>
        <CodeViewer.Title
            title="src/utils/color.ts"
        />
        <CodeViewer.Actions>
            <Button
                accessibilityLabel="Search"
                onPress={handleSearch}
                variant="ghost"
                type="neutral"
                size="small"
                icon={SearchIcon}
            />
        </CodeViewer.Actions>
    </CodeViewer.Header>
    <CodeViewer.Footer
        title="3 lines changed"
    >
        <Button
            onPress={handleShowAll}
            title="Show all"
            variant="ghost"
            type="neutral"
            size="small"
        />
    </CodeViewer.Footer>
</CodeViewer>`;

const LINE_STATES_CODE = `<CodeViewer
    lineStates={{
        46: "highlighted",
        48: "removed",
        49: "added",
        50: "added"
    }}
    language="typescript"
    code={snippet}
    startLine={41}
/>`;

const WRAP_CODE = `<CodeViewer
    isDefaultWrapped={true}
    language="typescript"
    code={longLine}
>
    <CodeViewer.Header>
        <CodeViewer.Title
            title="message.ts"
        />
        <CodeViewer.Actions />
    </CodeViewer.Header>
</CodeViewer>

<CodeViewer
    onWrappedChange={setIsWrapped}
    isWrapped={isWrapped}
    code={longLine}
>
    <CodeViewer.Header>
        <CodeViewer.Title
            title="message.ts"
        />
        <CodeViewer.Actions
            isWrapToggleVisible={false}
        />
    </CodeViewer.Header>
</CodeViewer>`;

const DANGER_CODE = `<CodeViewer
    type="danger"
    code={stackTrace}
>
    <CodeViewer.Header>
        <CodeViewer.Title
            title="TypeError"
        />
    </CodeViewer.Header>
</CodeViewer>`;

const SMALL_CODE = `<CodeViewer
    language="typescript"
    size="small"
    code={snippet}
/>`;

const COLOR_CODE = [
    "export function getSurfaceRelativeColor(",
    "    surface: string,",
    "    ground: string,",
    "    color: string,",
    "): string {",
    "    const [r, g, b] = parseColor(color);",
    "    const [sr, sg, sb] = parseColor(surface);",
    "    const mix = ground === \"#fff\" ? 0.5 : 0.3;",
    "    const mix = surfaceMix(surface, ground);",
    "    const blend = (c: number, s: number) => c + (s - c) * mix;",
    "    return toHex(blend(r, sr), blend(g, sg), blend(b, sb));",
    "}"
].join("\n");

const LINE_STATES = {
    46: "highlighted",
    48: "removed",
    49: "added",
    50: "added"
} as const;

const LONG_LINE_CODE = "const message = \"This long line wraps to the width of its container instead of scrolling sideways, and continuation lines get no number.\";";

const ERROR_CODE = [
    "TypeError: Cannot read properties of undefined (reading 'amount')",
    "    at getTotal (report.ts:12:24)",
    "    at getReport (report.ts:7:18)",
    "    at renderInvoices (invoices.tsx:31:5)"
].join("\n");

const WIDE_STYLE = {
    width: "100%"
} as const;

export const getToc = (t: DocsTranslate) => getSectionToc([
    {
        title: t("section.overview"),
        id: "overview"
    },
    {
        title: t("codeViewerPage.languagesTitle"),
        id: "languages"
    },
    {
        title: t("codeViewerPage.foldingTitle"),
        id: "folding"
    },
    {
        title: t("codeViewerPage.headerTitle"),
        id: "header-footer"
    },
    {
        title: t("codeViewerPage.lineStatesTitle"),
        id: "line-states"
    },
    {
        title: t("codeViewerPage.wrapTitle"),
        id: "wrapping"
    },
    {
        title: t("codeViewerPage.errorTitle"),
        id: "error-output"
    },
    {
        title: t("codeViewerPage.sizeTitle"),
        id: "size"
    },
    {
        title: t("codeViewerPage.appearanceTitle"),
        id: "appearance"
    },
    {
        title: t("section.accessibility"),
        id: "accessibility"
    },
    {
        title: t("section.props"),
        id: "props"
    }
], NAME, t);

const CodeViewerPage = () => {
    const {
        t
    } = useDocsI18n();

    const {
        radiuses
    } = NCoreUIKitTheme.useContext();

    const [
        isAutoFold,
        setIsAutoFold
    ] = useState(true);

    const [
        isCollapsible,
        setIsCollapsible
    ] = useState(true);

    const [
        isWrapped,
        setIsWrapped
    ] = useState(false);

    const handleAutoFoldToggle = () => setIsAutoFold(!isAutoFold);

    const handleWrappedToggle = () => setIsWrapped(!isWrapped);

    const handleNothing = () => undefined;

    const handleCollapsibleToggle = () => setIsCollapsible(!isCollapsible);

    return <Article>
        <ComponentHeader
            lead={t("codeViewerPage.lead")}
            name={NAME}
        />
        <Section
            title={t("section.overview")}
            id="overview"
        >
            <Paragraph>
                {t("codeViewerPage.overviewText")}
            </Paragraph>
            <Preview
                caption={t("codeViewerPage.overviewCaption")}
            >
                <CodeViewer
                    language="typescript"
                    style={WIDE_STYLE}
                    code={REPORT_CODE}
                />
            </Preview>
            <CodeBlock
                code={USAGE_CODE}
                language="tsx"
            />
        </Section>
        <AnatomySection
            name={NAME}
        />
        <Section
            title={t("codeViewerPage.languagesTitle")}
            id="languages"
        >
            <Paragraph>
                {t("codeViewerPage.languagesText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <CodeViewer
                    isLineNumberVisible={false}
                    code={BASH_CODE}
                    language="bash"
                />
                <CodeViewer
                    code={SQL_CODE}
                    language="sql"
                />
                <CodeViewer
                    code={PYTHON_CODE}
                    language="python"
                />
            </Preview>
        </Section>
        <Section
            title={t("codeViewerPage.foldingTitle")}
            id="folding"
        >
            <Paragraph>
                {t("codeViewerPage.foldingText")}
            </Paragraph>
            <Preview
                caption={t("codeViewerPage.foldingCaption")}
            >
                <CodeViewer
                    isCollapsible={isCollapsible}
                    language="typescript"
                    autoFold={isAutoFold}
                    autoFoldMaxLines={2}
                    style={WIDE_STYLE}
                    code={REPORT_CODE}
                />
                <Switch
                    onPress={handleAutoFoldToggle}
                    isChecked={isAutoFold}
                    title="autoFold"
                />
                <Switch
                    onPress={handleCollapsibleToggle}
                    isChecked={isCollapsible}
                    title="isCollapsible"
                />
            </Preview>
            <CodeBlock
                code={FOLDING_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("codeViewerPage.headerTitle")}
            id="header-footer"
        >
            <Paragraph>
                {t("codeViewerPage.headerText")}
            </Paragraph>
            <Preview
                caption={t("codeViewerPage.headerCaption")}
            >
                <CodeViewer
                    lineStates={LINE_STATES}
                    language="typescript"
                    style={WIDE_STYLE}
                    code={COLOR_CODE}
                    startLine={41}
                >
                    <CodeViewer.Header>
                        <CodeViewer.Title
                            title="src/utils/color.ts"
                        />
                        <CodeViewer.Actions>
                            <Button
                                accessibilityLabel={t("codeViewerPage.search")}
                                onPress={handleNothing}
                                icon={SearchIcon}
                                variant="ghost"
                                type="neutral"
                                size="small"
                            />
                        </CodeViewer.Actions>
                    </CodeViewer.Header>
                    <CodeViewer.Footer
                        title={t("codeViewerPage.footerSummary")}
                    >
                        <Button
                            title={t("codeViewerPage.showAll")}
                            onPress={handleNothing}
                            variant="ghost"
                            type="neutral"
                            size="small"
                        />
                    </CodeViewer.Footer>
                </CodeViewer>
            </Preview>
            <CodeBlock
                code={HEADER_CODE}
                language="tsx"
            />
            <Paragraph>
                {t("codeViewerPage.headerDefaultText")}
            </Paragraph>
        </Section>
        <Section
            title={t("codeViewerPage.lineStatesTitle")}
            id="line-states"
        >
            <Paragraph>
                {t("codeViewerPage.lineStatesText")}
            </Paragraph>
            <Preview
                caption={t("codeViewerPage.lineStatesCaption")}
            >
                <CodeViewer
                    lineStates={LINE_STATES}
                    language="typescript"
                    style={WIDE_STYLE}
                    code={COLOR_CODE}
                    startLine={41}
                />
            </Preview>
            <CodeBlock
                code={LINE_STATES_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("codeViewerPage.wrapTitle")}
            id="wrapping"
        >
            <Paragraph>
                {t("codeViewerPage.wrapText")}
            </Paragraph>
            <Preview
                caption={t("codeViewerPage.wrapCaption")}
                isStacked={true}
            >
                <CodeViewer
                    isDefaultWrapped={true}
                    code={LONG_LINE_CODE}
                    language="typescript"
                    style={WIDE_STYLE}
                >
                    <CodeViewer.Header>
                        <CodeViewer.Title
                            title="message.ts"
                        />
                        <CodeViewer.Actions />
                    </CodeViewer.Header>
                </CodeViewer>
                <CodeViewer
                    onWrappedChange={setIsWrapped}
                    code={LONG_LINE_CODE}
                    language="typescript"
                    isWrapped={isWrapped}
                    style={WIDE_STYLE}
                >
                    <CodeViewer.Header>
                        <CodeViewer.Title
                            title="message.ts"
                        />
                        <CodeViewer.Actions
                            isWrapToggleVisible={false}
                        />
                    </CodeViewer.Header>
                </CodeViewer>
                <Switch
                    onPress={handleWrappedToggle}
                    isChecked={isWrapped}
                    title="isWrapped"
                />
            </Preview>
            <CodeBlock
                code={WRAP_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("codeViewerPage.errorTitle")}
            id="error-output"
        >
            <Paragraph>
                {t("codeViewerPage.errorText")}
            </Paragraph>
            <Preview>
                <CodeViewer
                    isLineNumberVisible={false}
                    style={WIDE_STYLE}
                    code={ERROR_CODE}
                    type="danger"
                >
                    <CodeViewer.Header>
                        <CodeViewer.Title
                            title="TypeError"
                        />
                    </CodeViewer.Header>
                </CodeViewer>
            </Preview>
            <CodeBlock
                code={DANGER_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("codeViewerPage.sizeTitle")}
            id="size"
        >
            <Paragraph>
                {t("codeViewerPage.sizeText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <CodeViewer
                    style={WIDE_STYLE}
                    code={PYTHON_CODE}
                    language="python"
                />
                <CodeViewer
                    style={WIDE_STYLE}
                    code={PYTHON_CODE}
                    language="python"
                    size="small"
                />
            </Preview>
            <CodeBlock
                code={SMALL_CODE}
                language="tsx"
            />
        </Section>
        <Section
            title={t("codeViewerPage.appearanceTitle")}
            id="appearance"
        >
            <Paragraph>
                {t("codeViewerPage.appearanceText")}
            </Paragraph>
            <Preview
                isStacked={true}
            >
                <CodeViewer
                    languageLabel={t("codeViewerPage.terminal")}
                    isLineNumberVisible={false}
                    code={BASH_CODE}
                    language="bash"
                />
                <Card>
                    <Card.Content>
                        <CodeViewer
                            borderRadius={radiuses.form}
                            isLineNumberVisible={false}
                            code={BASH_CODE}
                            language="bash"
                        />
                    </Card.Content>
                </Card>
            </Preview>
        </Section>
        <Section
            title={t("section.accessibility")}
            id="accessibility"
        >
            <BulletList
                items={[
                    t("codeViewerPage.a11yFigure"),
                    t("codeViewerPage.a11yLtr"),
                    t("codeViewerPage.a11yNumbers"),
                    t("codeViewerPage.a11yCopy"),
                    t("codeViewerPage.a11yFold"),
                    t("codeViewerPage.a11ySigns"),
                    t("codeViewerPage.a11yLineState"),
                    t("codeViewerPage.a11yWrap"),
                    t("codeViewerPage.a11yActions"),
                    t("codeViewerPage.a11yKeyboard")
                ]}
            />
        </Section>
        <Section
            title={t("section.props")}
            id="props"
        >
            <PropsTable
                rows={[
                    {
                        description: t("codeViewerPage.propCode"),
                        type: "string",
                        name: "code"
                    },
                    {
                        description: t("codeViewerPage.propLanguage"),
                        name: "language",
                        type: "string"
                    },
                    {
                        description: t("codeViewerPage.propLanguageLabel"),
                        name: "languageLabel",
                        type: "string"
                    },
                    {
                        description: t("codeViewerPage.propIsLineNumberVisible"),
                        defaultValue: "true · danger: false",
                        name: "isLineNumberVisible",
                        type: "boolean"
                    },
                    {
                        description: t("codeViewerPage.propIsCollapsible"),
                        defaultValue: "true · danger: false",
                        name: "isCollapsible",
                        type: "boolean"
                    },
                    {
                        description: t("codeViewerPage.propAutoFold"),
                        defaultValue: "false",
                        name: "autoFold",
                        type: "boolean"
                    },
                    {
                        description: t("codeViewerPage.propAutoFoldMaxLines"),
                        name: "autoFoldMaxLines",
                        defaultValue: "60",
                        type: "number"
                    },
                    {
                        description: t("codeViewerPage.propStartLine"),
                        defaultValue: "1",
                        name: "startLine",
                        type: "number"
                    },
                    {
                        type: "Partial<Record<number, \"highlighted\" | \"added\" | \"removed\">>",
                        description: t("codeViewerPage.propLineStates"),
                        name: "lineStates"
                    },
                    {
                        description: t("codeViewerPage.propIsWrapped"),
                        name: "isWrapped",
                        type: "boolean"
                    },
                    {
                        description: t("codeViewerPage.propIsDefaultWrapped"),
                        name: "isDefaultWrapped",
                        defaultValue: "false",
                        type: "boolean"
                    },
                    {
                        description: t("codeViewerPage.propOnWrappedChange"),
                        type: "(isWrapped: boolean) => void",
                        name: "onWrappedChange"
                    },
                    {
                        description: t("codeViewerPage.propSize"),
                        type: "\"medium\" | \"small\"",
                        defaultValue: "\"medium\"",
                        name: "size"
                    },
                    {
                        description: t("codeViewerPage.propType"),
                        type: "\"neutral\" | \"danger\"",
                        defaultValue: "\"neutral\"",
                        name: "type"
                    },
                    {
                        description: t("codeViewerPage.propMaxHeight"),
                        name: "maxHeight",
                        type: "number"
                    },
                    {
                        description: t("codeViewerPage.propBorderRadius"),
                        name: "borderRadius",
                        type: "number"
                    },
                    {
                        description: t("codeViewerPage.propAccessibilityLabel"),
                        name: "accessibilityLabel",
                        type: "string"
                    },
                    {
                        description: t("codeViewerPage.propRole"),
                        name: "role",
                        type: "Role"
                    },
                    {
                        type: "{ themeKey, paletteKey, sharpness, gapPropagation }",
                        description: t("codeViewerPage.propCustomTheme"),
                        name: "customTheme"
                    },
                    {
                        description: t("codeViewerPage.propCustomLocalize"),
                        type: "{ activeLocale }",
                        name: "customLocalize"
                    }
                ]}
            />
            <Paragraph
                isMuted={true}
            >
                {t("codeViewerPage.propsNote")}
            </Paragraph>
            <Paragraph>
                {t("codeViewerPage.partsHeader")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("codeViewerPage.partHeaderChildren"),
                        name: "children",
                        type: "ReactNode"
                    }
                ]}
            />
            <Paragraph>
                {t("codeViewerPage.partsTitle")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("codeViewerPage.partTitleTitle"),
                        name: "title",
                        type: "string"
                    },
                    {
                        description: t("codeViewerPage.partTitleIcon"),
                        type: "NCoreUIKitIcon",
                        name: "icon"
                    }
                ]}
            />
            <Paragraph>
                {t("codeViewerPage.partsActions")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("codeViewerPage.partActionsChildren"),
                        name: "children",
                        type: "ReactNode"
                    },
                    {
                        description: t("codeViewerPage.partActionsWrapToggle"),
                        name: "isWrapToggleVisible",
                        defaultValue: "true",
                        type: "boolean"
                    }
                ]}
            />
            <Paragraph>
                {t("codeViewerPage.partsFooter")}
            </Paragraph>
            <PropsTable
                rows={[
                    {
                        description: t("codeViewerPage.partFooterTitle"),
                        name: "title",
                        type: "string"
                    },
                    {
                        description: t("codeViewerPage.partFooterChildren"),
                        name: "children",
                        type: "ReactNode"
                    }
                ]}
            />
        </Section>
        <ExampleSection
            name={NAME}
        />
    </Article>;
};
export default CodeViewerPage;
